import { useEffect, useState } from "react";
import { CustomDataGrid } from "../../../components/CustomDataGrid"
import { GridColDef } from "@mui/x-data-grid";
import { baseURL } from "../../../config/api";
import { Link, useNavigate } from "react-router-dom";
import { Menu, MenuItem, Stack } from "@mui/material";
import { formatDateTimeForDisplay } from "../../../utils/getUserDateFormat";

const filterItems = [
    {
        id: 0,
        label: "Season Name",
    },
    {
        id: 1,
        label: "Code",
    },
    {
        id: 2,
        label: "Season Type",
    },
    {
        id: 3,
        label: "Active",
    },
]

export const Seasons = () => {
    const navigate = useNavigate();
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
    });
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [selectedFilter, setSelectedFilter] = useState<string>(filterItems[0].label);
    const [filterValue, setFilterValue] = useState(0);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Season Name",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Link
                        to="/admin/settings/seasons/edit-season"
                        state={params?.id}
                        className="underline text-[#0000ff]"
                    >
                        {params?.value}
                    </Link>
                );
            },
        },
        {
            field: "code",
            headerName: "Code",
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: "season_type",
            headerName: "Season Type",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) =>
                params?.value
                    ? params.value.charAt(0).toUpperCase() +
                    params.value.slice(1)
                    : "",
        },
        {
            field: "start_date",
            headerName: "Start Date",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => (
                <span>
                    {formatDateTimeForDisplay(params.value)}
                </span>
            )
        },
        {
            field: "end_date",
            headerName: "End Date",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => (
                <span>
                    {formatDateTimeForDisplay(params.value)}
                </span>
            )
        },
        {
            field: "is_active",
            headerName: "Active",
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) =>
                params?.value ? "Yes" : "No",
        },
    ];

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };


    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 1,
                    pageSize: paginationModel?.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);


    useEffect(() => {
        if (searchValue !== "") {
            filterApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const filterApiCall = (newPaginationModel: any) => {
        setLoading(true);

        let url = `${baseURL}seasons/search?page=${newPaginationModel.page}&page_size=${newPaginationModel.pageSize}`;

        switch (filterValue) {
            case 0:
                url += `&name=${encodeURIComponent(searchValue)}`;
                break;

            case 1:
                url += `&code=${encodeURIComponent(searchValue)}`;
                break;

            case 2:
                url += `&season_type=${encodeURIComponent(searchValue)}`;
                break;

            case 3:
                url += `&is_active=${encodeURIComponent(searchValue)}`;
                break;

            default:
                break;
        }

        fetch(url)
            .then((res) => res.json())
            .then((data) => {
                const filterData = (data?.data?.items || []).map((item: any) => ({
                    ...item,
                    id: item.id,
                    isActive: item?.isActive ? "Yes" : "No",
                }));

                setSearchTableData(filterData);
                setTotalLength(data?.data?.pagination?.total || 0);
                setLoading(false);
            })
            .catch((error) => {
                setSearchTableData([]);
                setTotalLength(0);
                setLoading(false);
                console.error(error);
            });
    };

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 1,
                pageSize: paginationModel?.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false)
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {
        setLoading(true);
        fetch(`${baseURL}seasons/paginated?page=${newPaginationModel?.page}&page_size=${newPaginationModel?.pageSize}`)
            .then((res) => res.json())
            .then((data) => {
                const result = (data?.data?.items || []).map((item: any) => ({
                    ...item,
                    isActive: item?.isActive ? "Yes" : "No"
                }));

                setSearchTableData(result);
                setTotalLength(data?.data?.pagination?.total || 0);
                setLoading(false);
            })
            .catch(error => {
                setSearchTableData([]);
                setTotalLength(0);
                setLoading(false);
                console.error(error);
            });
    }

    return (
        <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
            <div className="w-full bg-white p-4 rounded-md">
                <div className="flex items-center justify-end py-2">
                    <div className="flex items-center gap-2.5">
                        <button className={`px-[8px] py-[6px] bg-primary rounded-lg border text-white flex items-center gap-1.5`}
                            onClick={() => navigate("/admin/settings/seasons/add-season")}
                        >
                            <span className="material-symbols-outlined text-[25px] font-light">
                                add
                            </span>
                            <span className="text-xs font-medium">Season</span>
                        </button>

                    </div>
                </div>
                <div className={`w-full border border-[#E4E7EC] rounded-[16px]`}>
                    <div className={`flex justify-between items-center p-4 border-b border-[#E4E7EC]`}>
                        <div className="flex items-center gap-1.5">
                            <h2 className={`text-lg font-semibold text-[#1D2939]`}>Seasons</h2>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid`}
                                    aria-controls={open ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={` text-[14px] font-medium text-black-rgba`}>{selectedFilter}</span>
                                        {open ? <span className={`material-symbols-outlined  text-[25px] font-extralight`}>
                                            keyboard_arrow_up
                                        </span> : <span className={`material-symbols-outlined  text-[25px] font-extralight`}>
                                            keyboard_arrow_down
                                        </span>}
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                            }
                                        }
                                    }}
                                >
                                    {
                                        filterItems?.map((filter, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                    onClick={() => {
                                                        setSelectedFilter(filter.label)
                                                        setFilterValue(filter.id)
                                                        setAnchorEl(null);

                                                    }}>
                                                    <span className={`text-[16px] font-medium text-black-rgba`}>{filter.label}</span>
                                                </MenuItem>

                                            </>

                                        ))
                                    }
                                </Menu>
                            </div>
                            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border shadow-sm`}>
                                <span className={`material-symbols-outlined text-[25px] font-light text-black-rgba`}>
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    className={`border-none outline-none w-full bg-transparent`}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <CustomDataGrid
                        filteredRows={searchTableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: "bg-[#F9FAFB]",
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>
        </div>
    )
}